import React, { useCallback, useMemo, useRef } from 'react';

import { Separator, Text } from '@change/ds-core-ui';
import { AiGeneratedTextIcon } from '@change/ds-icons';

import type { EmptyProps } from '@change-corgi/core/types';
import { parseHtml } from '@change-corgi/core-react/html';
import { Translate, useI18n, withI18nPlaceholders } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import I18N_PLACEHOLDERS from '#config/i18n/placeholders/petitionDetails/petition-details.yml';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { InfoTooltip } from '#src/app/shared/components/tooltips';
import { useToast } from '#src/app/shared/toast';

import type { NotHelpfulReason } from './components/AiSummaryFeedbackLinksStaggered';
import { AiSummaryFeedbackLinksStaggered } from './components/AiSummaryFeedbackLinksStaggered';
import { useAiSummaryImpression } from './hooks/useAiSummaryImpression';
import { storeFeedback, useStoredFeedbackDirection } from './utils/feedbackStorageUtils';
import { parseAiSummaryBullets } from './utils/parseAiSummaryBullets';

// oxlint-disable-next-line max-lines-per-function, max-statements
const AiSummaryComponent: React.FC<Props> = () => {
	const track = useTracking();
	const { translate } = useI18n();
	const { showToast } = useToast();
	const {
		data: {
			petition: { id: petitionId },
		},
	} = usePetitionDetailsPageData();
	const {
		data: {
			experiments: { aiSummaries },
		},
	} = usePetitionDetailsPageUserData();
	// Direction stored in a previous session ('yes'/'no'), read on mount from localStorage.
	// null = SSR/unknown, false = no feedback stored, 'yes'/'no' = direction known.
	const storedFeedbackDirection = useStoredFeedbackDirection(petitionId);

	const handleFeedbackYes = useCallback(() => {
		storeFeedback(petitionId, 'yes');
		void track('petition_page_ai_summary_feedback', {
			ai_summary_marked_helpful: true,
			petition_id: petitionId,
		});
		showToast({
			variant: 'success',
			message: translate('corgi.petition_details.ai_summary.toast_helpful'),
		});
	}, [petitionId, track, showToast, translate]);

	const handleFeedbackNoClick = useCallback(() => {
		void track('petition_page_ai_summary_feedback', {
			ai_summary_marked_helpful: false,
			petition_id: petitionId,
		});
	}, [petitionId, track]);

	const handleReasonSelected = useCallback(
		(reason: NotHelpfulReason) => {
			void track('ai_summary_feedback_reason_selected', {
				ai_summary_selected_reason: reason,
				marked_helpful: false,
				petition_id: petitionId,
			});
		},
		[petitionId, track],
	);

	const handleFeedbackNo = useCallback(
		(reasons: NotHelpfulReason[], details: string) => {
			storeFeedback(petitionId, 'no');
			void track('petition_page_ai_summary_feedback_reasons', {
				petition_id: petitionId,
				ai_summary_feedback_reasons: reasons,
				ai_summary_feedback_details: details,
			});
			void track('ai_summary_feedback_reasons_submitted', {
				marked_helpful: false,
				petition_id: petitionId,
				ai_summary_feedback_details: details.trim() || 'empty',
			});
			showToast({
				variant: 'success',
				message: translate('corgi.petition_details.ai_summary.toast_not_helpful'),
			});
		},
		[petitionId, track, showToast, translate],
	);

	// tooltipTrackedRef: true while the tooltip is "open" from a tracking perspective.
	// tooltipResetRef: pending timer to reset tracked state after a close event.
	const tooltipTrackedRef = useRef(false);
	const tooltipResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const handleTooltipEnter = useCallback(() => {
		// Cancel any pending reset — the cursor came back (desktop icon-swap re-enter)
		// or a new touch started before the previous reset fired.
		if (tooltipResetRef.current !== null) {
			clearTimeout(tooltipResetRef.current);
			tooltipResetRef.current = null;
		}
		if (!tooltipTrackedRef.current) {
			tooltipTrackedRef.current = true;
			void track('petition_page_ai_summary_tooltip_opened', {
				petition_id: petitionId,
			});
		}
	}, [petitionId, track]);

	const handleTooltipLeave = useCallback(() => {
		// Cursor left the element (desktop) — reset immediately.
		tooltipTrackedRef.current = false;
	}, []);

	const handleTooltipClose = useCallback(() => {
		// Tooltip closed. Schedule a reset with a short delay:
		// - Desktop: the icon-swap fires onMouseEnter within ~50ms, which cancels this.
		// - Mobile: no mouseenter follows a close-tap, so the reset executes and the
		//   next open-tap is trackable again.
		if (tooltipResetRef.current !== null) clearTimeout(tooltipResetRef.current);
		tooltipResetRef.current = setTimeout(() => {
			tooltipTrackedRef.current = false;
			tooltipResetRef.current = null;
		}, 150);
	}, []);

	const handleFeedbackCancelled = useCallback(() => {
		void track('petition_page_ai_summary_additional_feedback_cancelled', {
			petition_id: petitionId,
		});
	}, [petitionId, track]);

	const summary = aiSummaries?.summary;
	const bullets = useMemo(() => parseAiSummaryBullets(summary), [summary]);
	const impressionRef = useAiSummaryImpression(petitionId, bullets.length > 0);

	if (!bullets.length) {
		return null;
	}

	return (
		<section
			ref={impressionRef}
			aria-labelledby="ai-summary-heading"
			data-qa="ai-summary-section"
			data-testid="ai-summary-section"
		>
			<div className="mb-6 flex flex-col gap-4">
				<Separator role="presentation" />
				<div className="flex flex-col gap-2">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							<AiGeneratedTextIcon aria-hidden className="text-[#0D4DFB]" size="x-small" />
							<div className="flex items-center gap-1">
								<Text
									className="text-typography-strong"
									fontWeight="bold"
									id="ai-summary-heading"
									size="large"
									variant="body"
								>
									<Translate value="corgi.petition_details.ai_summary.section_description_label" />
								</Text>
								<InfoTooltip
									size={16}
									className="text-[#918A9D]"
									onMouseEnter={handleTooltipEnter}
									onMouseLeave={handleTooltipLeave}
									onTooltipClose={handleTooltipClose}
								>
									<Translate value="corgi.petition_details.ai_summary.feedback_source" />
								</InfoTooltip>
							</div>
						</div>
						<AiSummaryFeedbackLinksStaggered
							onFeedbackYes={handleFeedbackYes}
							onFeedbackNoClick={handleFeedbackNoClick}
							onFeedbackNo={handleFeedbackNo}
							onFeedbackCancelled={handleFeedbackCancelled}
							onReasonSelected={handleReasonSelected}
							storedFeedbackDirection={storedFeedbackDirection || null}
						/>
					</div>
					<Text as="div" className="text-typography-strong" size="large" variant="body">
						<ul className="m-0 flex list-disc flex-col gap-1 pl-6">
							{bullets.map((bullet) => (
								<li key={bullet}>{parseHtml(bullet)}</li>
							))}
						</ul>
					</Text>
				</div>
				<Separator role="presentation" />
			</div>
		</section>
	);
};

export const AiSummary = withI18nPlaceholders(I18N_PLACEHOLDERS, AiSummaryComponent);

type Props = EmptyProps;
